import type { ReactElement } from "react"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import DomainSearchPrompt from "@gatsby-plugin-domain-search/components/DomainSearchPrompt"
import DomainSearchSurfaceElement from "@gatsby-plugin-domain-search/components/DomainSearchSurfaceElement"
import DomainSearchSwitchElement from "@gatsby-plugin-domain-search/components/DomainSearchSwitchElement"
import SearchSectionUdsHeader from "@gatsby-plugin-domain-search/components/SearchSectionUdsHeader"
import useDomainSearchPromptInput from "@gatsby-plugin-domain-search/hooks/useDomainSearchPromptInput"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import type { SearchSectionFlatData } from "@gatsby-plugin-generic-page/fragments/searchSection"
import isEmpty from "~/lib/isEmpty"
import DomainSearch from "./components/DomainSearch"

function MainSearchSection({
  header,
  content,
  containerBackgroundColor,
  showAILinkOnRight,
  sectionSettings,
  domainExtensions,
  defaultExtensionIndex,
}: SearchSectionFlatData): ReactElement<SearchSectionFlatData> {
  const isSmallScreen = useBreakpoint("small", "large")

  const {
    value,
    setValueToRandomPrompt,
    setValue,
    onSubmitButtonClick,
    isPending,
  } = useDomainSearchPromptInput()

  const domainSearchInput = (
    <DomainSearch
      text={content}
      domainExtensions={domainExtensions}
      defaultExtensionIndex={defaultExtensionIndex}
    />
  )

  const domainSearchPrompt = (
    <DomainSearchPrompt
      value={value}
      isPending={isPending}
      setValue={setValue}
      onSubmitButtonClick={onSubmitButtonClick}
      onRandomizePrompt={setValueToRandomPrompt}
    />
  )

  const domainSearchTabs = (
    <DomainSearchSwitchElement
      domainSearchInput={domainSearchInput}
      domainSearchPrompt={domainSearchPrompt}
      isFooterAlignedRight={showAILinkOnRight}
    />
  )

  const headerElement = !isEmpty(header) ? (
    <SearchSectionUdsHeader {...header} />
  ) : null

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <ConditionalWrapper
          condition={!isEmpty(containerBackgroundColor)}
          wrapper={(children) => (
            <DomainSearchSurfaceElement
              backgroundColor={containerBackgroundColor}
              hasHeader={isSmallScreen ? false : !isEmpty(header)}
              isContentWide={showAILinkOnRight}
            >
              <BreakpointVisibilityOrg from="large">
                {headerElement}
              </BreakpointVisibilityOrg>

              {children}
            </DomainSearchSurfaceElement>
          )}
        >
          {isEmpty(containerBackgroundColor) ? headerElement : null}

          {domainSearchTabs}
        </ConditionalWrapper>
      </BaseContainer>
    </BaseSection>
  )
}

export default MainSearchSection
